/* eslint-disable */
function TermsAndConditions() {
  return (
    <div className="prose prose-gray max-w-none">
      <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Terms &amp; Conditions</h1>
      <p className="text-sm text-gray-700 mb-8">Effective date: April 2, 2026 · Last updated: April 2, 2026</p>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">1. Introduction</h2>
        <p className="text-gray-600 leading-relaxed">
          Welcome to Affiliar, an affiliate management platform operated by Affiliar Ltd. (&quot;Affiliar&quot;, &quot;we&quot;, &quot;us&quot;,
          or &quot;our&quot;). These Terms &amp; Conditions (&quot;Terms&quot;) govern your access to and use of the Affiliar platform,
          including all features, tools, dashboards, APIs, and related services (collectively, the &quot;Service&quot;).
        </p>
        <p className="text-gray-600 leading-relaxed mt-3">
          By registering an account or accessing the Service, you (&quot;Affiliate&quot;, &quot;you&quot;, or &quot;your&quot;) agree to be
          bound by these Terms. If you do not agree, you must not use the Service.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">2. Definitions</h2>
        <ul className="list-disc list-inside space-y-2 text-gray-600">
          <li><strong>Affiliate:</strong> A registered user who promotes Operator brands and earns commissions through the Affiliar platform.</li>
          <li><strong>Operator:</strong> An iGaming operator (casino, sportsbook, or similar) that uses Affiliar to manage its affiliate program.</li>
          <li><strong>Referred Player:</strong> An end-user who registers and deposits with an Operator through an Affiliate's unique tracking link.</li>
          <li><strong>Commission:</strong> Compensation earned by an Affiliate based on the activity of Referred Players under an agreed commission plan.</li>
          <li><strong>Net Gaming Revenue (NGR):</strong> Gross gaming revenue minus bonuses, chargebacks, taxes, and other applicable deductions as defined by the Operator.</li>
          <li><strong>Tracking Link:</strong> A unique URL assigned to an Affiliate for attribution of Referred Players.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">3. Eligibility and Account Registration</h2>
        <p className="text-gray-600 leading-relaxed">
          To use the Service, you must be at least 18 years of age, legally capable of entering into binding contracts,
          and not be a resident of any jurisdiction where participation in or promotion of iGaming is prohibited by law.
        </p>
        <p className="text-gray-600 leading-relaxed mt-3">
          You agree to provide accurate, current, and complete information during registration and to keep your account
          credentials confidential. You are solely responsible for all activity that occurs under your account. Notify
          us immediately at <span className="font-medium">support@affiliar.io</span> if you suspect unauthorized access.
        </p>
        <p className="text-gray-600 leading-relaxed mt-3">
          Affiliar reserves the right to decline or revoke registration at its sole discretion, particularly where
          regulatory, compliance, or reputational risks are identified.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">4. Affiliate Program Structure</h2>
        <p className="text-gray-600 leading-relaxed">
          Through Affiliar, Affiliates gain access to one or more Operator programs. Each program may have its own
          commission structures, promotional materials, restrictions, and payment schedules, which are presented within
          the platform. Affiliates must review and comply with the specific terms of each Operator program they join.
        </p>
        <p className="text-gray-600 leading-relaxed mt-3">
          Affiliar acts as a technology intermediary. Commission obligations, player data ownership, and brand guidelines
          remain the responsibility of the respective Operators. Affiliar is not liable for any Operator's failure to
          fulfill its obligations.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">5. Commission Plans and Payments</h2>
        <p className="text-gray-600 leading-relaxed">
          Commission plans available through Affiliar may include Revenue Share, Cost Per Acquisition (CPA), hybrid
          arrangements, or other structures as agreed between the Affiliate and the Operator. Applicable rates and
          structures are displayed in the platform dashboard.
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-600 mt-3">
          <li>Commissions are calculated based on verified activity during each billing period.</li>
          <li>Payments are processed according to the schedule defined by each Operator (typically monthly).</li>
          <li>Minimum payment thresholds may apply; unpaid balances below the threshold roll over to the next period.</li>
          <li>Affiliar and Operators reserve the right to withhold payment in cases of suspected fraud, self-referral, or breach of these Terms.</li>
          <li>All commission figures displayed are indicative until formally confirmed at period close.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">6. Prohibited Activities</h2>
        <p className="text-gray-600 leading-relaxed">You must not, and must not permit others to:</p>
        <ul className="list-disc list-inside space-y-2 text-gray-600 mt-3">
          <li>Generate artificial traffic, clicks, or referrals through automated means, bots, or fraudulent schemes.</li>
          <li>Self-refer or refer individuals with whom you have a direct financial interest in their gambling activity.</li>
          <li>Target minors or individuals in jurisdictions where iGaming is prohibited.</li>
          <li>Use spam, unsolicited email, or deceptive advertising to drive traffic.</li>
          <li>Misrepresent Operator brands, bonus terms, or odds in marketing materials.</li>
          <li>Reproduce, redistribute, or modify Affiliar's platform, code, or proprietary data.</li>
          <li>Use the Service for money laundering or any other illegal activity.</li>
          <li>Violate any applicable advertising standards, gambling regulations, or data protection laws.</li>
        </ul>
        <p className="text-gray-600 leading-relaxed mt-3">
          Violation of these prohibitions may result in immediate account suspension, forfeiture of pending commissions,
          and legal action where appropriate.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">7. Intellectual Property</h2>
        <p className="text-gray-600 leading-relaxed">
          All content, software, trademarks, logos, and materials comprising the Affiliar platform are the exclusive
          intellectual property of Affiliar Ltd. or its licensors. No license is granted other than the limited right
          to access and use the Service as contemplated by these Terms.
        </p>
        <p className="text-gray-600 leading-relaxed mt-3">
          Operator-supplied creative assets (banners, landing pages, promotional copy) remain the property of the
          respective Operator. Affiliates are granted a revocable, non-exclusive license to use such materials solely
          for the purpose of promoting the Operator's products in accordance with applicable guidelines.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">8. Data Protection</h2>
        <p className="text-gray-600 leading-relaxed">
          Your use of the Service is subject to our <a href="/privacy-policy" className="text-blue-600 underline">Privacy Policy</a>,
          which is incorporated into these Terms by reference. You acknowledge that in processing Referred Player data
          you may act as a data controller in your own right and are responsible for compliance with applicable data
          protection legislation including GDPR.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">9. Disclaimer of Warranties</h2>
        <p className="text-gray-600 leading-relaxed">
          The Service is provided on an &quot;as is&quot; and &quot;as available&quot; basis. Affiliar makes no warranties, express or
          implied, including but not limited to merchantability, fitness for a particular purpose, or uninterrupted
          availability. We do not warrant that the Service will be error-free or that defects will be corrected.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">10. Limitation of Liability</h2>
        <p className="text-gray-600 leading-relaxed">
          To the fullest extent permitted by law, Affiliar's aggregate liability to you for any claim arising out of
          or relating to these Terms or the Service shall not exceed the total commissions paid to you by Affiliar in
          the three (3) months preceding the claim. Affiliar shall not be liable for any indirect, incidental, special,
          consequential, or punitive damages, including loss of profits or data.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">11. Termination</h2>
        <p className="text-gray-600 leading-relaxed">
          Either party may terminate the affiliate relationship at any time by providing written notice. Affiliar may
          suspend or terminate your account immediately without notice if you breach these Terms or if continued
          operation poses a regulatory or reputational risk.
        </p>
        <p className="text-gray-600 leading-relaxed mt-3">
          Upon termination, your right to use the Service ceases immediately. Commissions accrued prior to termination
          will be settled in accordance with the applicable payment schedule, subject to any fraud investigation or
          breach of these Terms.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">12. Changes to These Terms</h2>
        <p className="text-gray-600 leading-relaxed">
          Affiliar reserves the right to modify these Terms at any time. Material changes will be communicated via
          email or an in-platform notification at least 14 days before taking effect. Continued use of the Service
          after the effective date constitutes acceptance of the updated Terms.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">13. Governing Law and Dispute Resolution</h2>
        <p className="text-gray-600 leading-relaxed">
          These Terms are governed by and construed in accordance with the laws of Malta, without regard to its conflict
          of law principles. Any disputes shall be subject to the exclusive jurisdiction of the courts of Malta. Nothing
          in this clause prevents either party from seeking urgent injunctive relief in any competent jurisdiction.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">14. Contact</h2>
        <p className="text-gray-600 leading-relaxed">
          If you have questions about these Terms, please contact us at:
        </p>
        <address className="not-italic text-gray-600 mt-2 space-y-1">
          <p className="font-medium">Affiliar Ltd.</p>
          <p>Email: <a href="mailto:legal@affiliar.io" className="text-blue-600 underline">legal@affiliar.io</a></p>
        </address>
      </section>
    </div>
  );
}

export default TermsAndConditions;
